import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import {
  createUserWithEmailAndPassword,
  deleteUser
} from "firebase/auth";
import { httpsCallable } from "firebase/functions";
import { auth, functions } from "../../config/firebase";
import { 
  UserPlus, 
  Lock, 
  Mail, 
  User, 
  Loader2, 
  CheckCircle, 
  ShieldAlert 
} from "lucide-react";

// Assets Oficiais
import logoOriginal from "../../assets/images/logo_frotabaase_transp.png";
import illustrationLight from "../../assets/images/illustration-light.png";

const InviteRegisterPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const brandColor = "#00a99d";

  const emailParam = (searchParams.get("email") || "").trim().toLowerCase();
  const companyParam = searchParams.get("company") || "";
  const inviteToken = searchParams.get("token") || "";

  const [view, setView] = useState("loading"); // loading, form, processing, success, invalid
  const [error, setError] = useState("");
  const [formData, setFormData] = useState({
    name: "",
    password: "",
    confirmPassword: ""
  });

  // --- 1. VALIDAÇÃO ESTRUTURAL DO LINK ---
  // A validade real do convite é decidida exclusivamente pela Cloud Function
  // depois que o usuário estiver autenticado.
  useEffect(() => {
    if (!emailParam || !companyParam || !inviteToken) {
      setView("invalid");
      return;
    }
    setView("form");
  }, [emailParam, companyParam, inviteToken]);

  /** @param {React.FormEvent} e */
  const handleRegister = async (e) => {
    e.preventDefault();
    if (formData.password !== formData.confirmPassword) {
      setError("As senhas não coincidem.");
      return;
    }

    setError("");
    setView("processing");

    let createdUser = null;
    let createdInThisAttempt = false;

    try {
      // 1. Cria a identidade apenas se este convite ainda não estiver
      // autenticado localmente. Isso permite retry após falha transitória.
      const currentUserEmail = String(auth.currentUser?.email || "")
        .trim()
        .toLowerCase();

      if (auth.currentUser && currentUserEmail === emailParam) {
        createdUser = auth.currentUser;
      } else {
        const userCredential = await createUserWithEmailAndPassword(
          auth,
          emailParam,
          formData.password
        );
        createdUser = userCredential.user;
        createdInThisAttempt = true;
      }

      // 2. Role, companyId, membership e consumo do convite
      // são definidos exclusivamente pela Cloud Function.
      const acceptCompanyInvite = httpsCallable(functions, "acceptCompanyInvite");
      await acceptCompanyInvite({
        companyId: companyParam,
        inviteToken,
        name: formData.name,
        environment: process.env.REACT_APP_ENV
      });

      setView("success");
      setTimeout(() => navigate("/"), 2000);

    } catch (error) {
      const err = /** @type {any} */ (error);
      console.error("Erro no registro:", err);
      
      const deterministicFailure = [
        "functions/permission-denied",
        "functions/invalid-argument",
        "functions/not-found",
        "functions/failed-precondition"
      ].includes(err.code);

      if (createdUser && createdInThisAttempt && deterministicFailure) {
        try {
          await deleteUser(createdUser);
          console.log("Identidade removida após rejeição determinística do convite.");
        } catch (rollbackErr) {
          console.error("Falha ao remover identidade rejeitada:", rollbackErr);
        }
      }

      setView("form");

      if (err.code === "auth/email-already-in-use") {
        setError("E-mail já cadastrado.");
      } else if (deterministicFailure) {
        setError("Convite inválido, expirado ou já utilizado.");
      } else {
        setError("Não foi possível concluir a ativação. Tente novamente.");
      }
    }
  };

  // --- RENDERS AUXILIARES ---

  if (view === "loading") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white dark:bg-dark-bg">
        <Loader2 className="w-10 h-10 animate-spin text-[#00a99d]" />
      </div>
    );
  }

  if (view === "invalid") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-dark-bg p-6">
        <div className="text-center max-w-sm">
          <ShieldAlert className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-800 dark:text-white mb-2">Link Inválido ou Expirado</h2>
          <p className="text-gray-500 dark:text-gray-400 mb-6 text-sm">Este convite não foi encontrado ou já foi utilizado para criar uma conta.</p>
          <Link to="/login" className="px-8 py-3 bg-gray-200 dark:bg-dark-border text-gray-700 dark:text-white rounded-full font-bold hover:bg-gray-300 transition-colors">Voltar ao Login</Link>
        </div>
      </div>
    );
  }

  if (view === "success") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white dark:bg-dark-bg">
        <div className="text-center animate-in zoom-in duration-300">
          <CheckCircle className="w-20 h-20 text-green-500 mx-auto mb-6" />
          <h2 className="text-3xl font-bold text-gray-800 dark:text-white mb-2">Tudo pronto!</h2>
          <p className="text-gray-500">Acesso liberado. Entrando no sistema...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen w-full font-sans bg-white dark:bg-dark-bg overflow-hidden">
      <div className="hidden lg:flex relative w-1/2 flex-col justify-between overflow-hidden">
        <img src={illustrationLight} alt="Background" className="absolute inset-0 w-full h-full object-cover z-0" />
        <div className="relative z-10 p-12">
          <img src={logoOriginal} alt="Logo" className="h-16 md:h-20 w-auto object-contain" />
        </div>
        <div className="relative z-10 p-12 text-center pb-20">
          <h2 className="text-4xl font-light text-white leading-tight">Quase lá!<br /><span className="font-bold">Sua gestão começa aqui.</span></h2>
        </div>
      </div>

      <div className="flex flex-1 justify-center items-center p-8 lg:p-24 bg-white dark:bg-dark-card">
        <div className="w-full max-w-md">
          <h1 className="text-3xl font-light text-gray-800 dark:text-white mb-2">Bem-vindo à</h1>
          <h2 className="text-4xl font-bold mb-10" style={{ color: brandColor }}>
            sua imobiliária
          </h2>
          
          <form onSubmit={handleRegister} className="space-y-10">
            <div className="relative group">
              <label className="text-xs text-gray-400 uppercase font-bold tracking-wider">Seu Nome Completo</label>
              <div className="flex items-center border-b-2 border-gray-300 dark:border-dark-border focus-within:border-[#00a99d] transition-colors">
                <User className="w-5 h-5 text-gray-400 mr-2" />
                <input 
                  type="text" 
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full py-3 bg-transparent text-gray-800 dark:text-white outline-none" 
                  placeholder="Ex: Carlos Matos"
                />
              </div>
            </div>

            <div className="relative opacity-60">
              <label className="text-xs text-gray-400 uppercase font-bold tracking-wider">E-mail de Acesso</label>
              <div className="flex items-center border-b-2 border-gray-100 dark:border-dark-border">
                <Mail className="w-5 h-5 text-gray-400 mr-2" />
                <input type="text" readOnly value={emailParam} className="w-full py-3 bg-transparent text-gray-400 cursor-not-allowed outline-none" />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
              <div className="relative group">
                <label className="text-xs text-gray-400 uppercase font-bold tracking-wider">Defina sua Senha</label>
                <div className="flex items-center border-b-2 border-gray-300 dark:border-dark-border focus-within:border-[#00a99d] transition-colors">
                  <Lock className="w-5 h-5 text-gray-400 mr-2" />
                  <input 
                    type="password" 
                    required
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="w-full py-3 bg-transparent text-gray-800 dark:text-white outline-none" 
                    placeholder="******"
                  />
                </div>
              </div>
              <div className="relative group">
                <label className="text-xs text-gray-400 uppercase font-bold tracking-wider">Confirme a Senha</label>
                <div className="flex items-center border-b-2 border-gray-300 dark:border-dark-border focus-within:border-[#00a99d] transition-colors">
                  <Lock className="w-5 h-5 text-gray-400 mr-2" />
                  <input 
                    type="password" 
                    required
                    value={formData.confirmPassword}
                    onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                    className="w-full py-3 bg-transparent text-gray-800 dark:text-white outline-none" 
                    placeholder="******"
                  />
                </div>
              </div>
            </div>

            {error && <p className="text-xs text-red-500 font-bold bg-red-50 dark:bg-red-900/20 p-2 rounded text-center">{error}</p>}

            <button
              type="submit"
              disabled={view === "processing"}
              className="w-full py-4 rounded-full text-white font-bold transition-all hover:scale-105 shadow-xl shadow-[#00a99d]/20 active:scale-95 flex justify-center items-center"
              style={{ backgroundColor: brandColor }}
            >
              {view === "processing" ? <Loader2 className="animate-spin mr-2" /> : <UserPlus className="mr-2 w-5 h-5" />}
              {view === "processing" ? "Configurando conta..." : "Criar minha conta"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default InviteRegisterPage;